import type { IModel } from '../stores/model';
import type { IMethod } from '../stores/model/metamodel/method';

/**
 * Shared empty list so callers that depend on referential equality (the
 * add-transition auto-cancel effect) do not see a fresh `[]` every render.
 * A new empty array retriggered that effect, which called onCancel → setState
 * until React error #185 (the production stack named React Flow's setEdges).
 */
const NO_METHODS: IMethod[] = [];

function methodsOrNone(methods: IMethod[]): IMethod[] {
  return methods.length === 0 ? NO_METHODS : methods;
}

/**
 * Custom hook that produces the list of methods for a transition given the ids
 * of the source and target state.
 *
 * @param model the MERODE model
 * @param objectTypeId the id of the object type
 * @param sourceStateId the id of the source state
 * @param targetStateId the id of the target state
 *
 * @return the list of applicable methods
 */
export function useMethodsForTransition(
  model: IModel,
  objectTypeId: string,
  sourceStateId: string,
  targetStateId: string
): IMethod[] {
  const objectType = model.metamodel.getObjectTypeById(objectTypeId);
  const sourceState = model.metamodel.getStateById(sourceStateId);
  const targetState = model.metamodel.getStateById(targetStateId);
  if (objectType == null || sourceState == null || targetState == null) {
    return NO_METHODS;
  } else if (sourceState.type === 'INITIAL_STATE' && targetState.type === 'STATE') {
    // create
    return methodsOrNone(objectType.methods.filter(m => m.type === 'CREATE'));
  } else if (sourceState.type === 'STATE' && targetState.type === 'STATE') {
    // modify
    return methodsOrNone(objectType.methods.filter(m => m.type === 'MODIFY'));
  } else if (sourceState.type === 'STATE' && targetState.type === 'FINAL_STATE') {
    // end
    return methodsOrNone(objectType.methods.filter(m => m.type === 'END'));
  } else {
    // Some combination that is now allowed
    return NO_METHODS;
  }
}
